import { useState, useEffect, useRef, useMemo } from "react";
import { useWebsite } from "../../../context/WebsiteContext";

import { WebInput, WebButton } from "./shared";
import SearchResults from "./SearchResults";

export default function Home() {
  const { currentURL, navigateToURL, setPageTitle, setPageIcon } = useWebsite();

  const [searchTextValue, setSearchTextValue] = useState("");
  const marqueeRef = useRef<HTMLDivElement | null>(null);
  const marqueeInnerRef = useRef<HTMLDivElement | null>(null);

  const searchQuery = useMemo(() => {
    try {
      const url = new URL(currentURL);
      return url.searchParams.get("q") || "";
    } catch {
      return "";
    }
  }, [currentURL]);

  const isSearchResults = searchQuery.trim().length > 0;

  useEffect(() => {
    if (!isSearchResults) {
      setPageTitle("Internet Home");
      setPageIcon(undefined);
    }
  }, [currentURL, setPageTitle, setPageIcon, isSearchResults]);

  useEffect(() => {
    function updateGap() {
      const width = marqueeRef.current?.getBoundingClientRect().width ?? 0;
      if (marqueeInnerRef.current)
        marqueeInnerRef.current.style.setProperty("--marquee-gap", `${width}px`);
    }
    updateGap();
    window.addEventListener("resize", updateGap);
    return () => window.removeEventListener("resize", updateGap);
  }, []);

  if (isSearchResults) {
    return <SearchResults />;
  }

  return (
    <div
      style={{
        display: "flex",
        flexGrow: 1,
        alignItems: "flex-start",
        justifyContent: "flex-start",
        width: "100%",
        padding: "10px",
        backgroundImage: "url(./backgrounds/clouds.gif)",
        backgroundRepeat: "repeat",
        backgroundAttachment: "scroll",
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      <table
        style={{
          width: "100%",
          maxWidth: "760px",
          margin: "0 auto",
          tableLayout: "fixed",
          borderCollapse: "separate",
          borderSpacing: 0,
          border: "2px solid #000",
          boxSizing: "border-box",
        }}
      >
        <tbody>
          <tr>
            <td style={{ backgroundColor: "rgba(246, 234, 234, 0.4)", backgroundImage: "url(./backgrounds/clouds.gif)", backgroundRepeat: "repeat", padding: "10px", width: "100%", textAlign: "center", borderBottom: "1px solid #000", boxSizing: "border-box" }}>
              <h1 style={{ textDecoration: "underline", margin: 0 }}>Welcome Home!</h1>
              <div
                style={{ fontSize: "14px", overflow: "hidden", whiteSpace: "nowrap", marginTop: "6px" }}
                ref={marqueeRef}
              >
                <style>{`
                  @keyframes marquee {
                    from { transform: translateX(0); }
                    to { transform: translateX(-50%); }
                  }
                  .marquee-inner {
                    display: inline-block;
                    white-space: nowrap;
                    will-change: transform;
                    animation: marquee 10s linear infinite;
                    font-style: italic;
                  }
                  .marquee-inner span { display: inline-block; padding-right: var(--marquee-gap, 2rem); }
                `}</style>
                <div className="marquee-inner" ref={marqueeInnerRef}>
                  <span>✨ Your gateway to the world of tomorrow. ✨</span>
                  <span>✨ Your gateway to the world of tomorrow. ✨</span>
                </div>
              </div>
            </td>
          </tr>

          <tr>
            <td style={{ backgroundColor: "rgba(255, 255, 255, 0.6)", backgroundImage: "url(./backgrounds/clouds.gif)", backgroundRepeat: "repeat", padding: "10px", borderBottom: "1px solid #000", boxSizing: "border-box" }}>
              <h3 style={{ marginTop: 0 }}>Search for Websites</h3>

              <WebInput
                placeholder="Use a keyboard to type your search query here..."
                style={{ width: "100%", boxSizing: "border-box" }}
                value={searchTextValue}
                onChange={(e) => setSearchTextValue(e.target.value)}
              />
              <div style={{ display: "flex", justifyContent: "center", marginTop: "8px" }}>
                <WebButton
                  label="Search"
                  style={{ maxWidth: "160px" }}
                  disabled={searchTextValue.trim().length === 0}
                  onClick={() => {
                    if (searchTextValue.trim().length === 0) return;
                    navigateToURL(`http://home.dom?q=${encodeURIComponent(searchTextValue)}`);
                  }}
                />
              </div>
            </td>
          </tr>

          <tr>
            <td style={{ backgroundColor: "rgba(246, 234, 234, 0.3)", backgroundImage: "url(./backgrounds/clouds.gif)", backgroundRepeat: "repeat", padding: "10px", borderBottom: "1px solid #000", textAlign: "center", boxSizing: "border-box" }}>
              <style>{`
                    .old-link a {
                      color: blue;
                      text-decoration: underline;
                      cursor: pointer;
                    }
                    .old-link a:hover {
                      background-color: yellow;
                      text-transform: uppercase;
                    }
                  `}</style>
              <h3 style={{ marginTop: 0 }}>Quick Web Links</h3>
              <table style={{ borderCollapse: "collapse", width: "100%", textAlign: "center", border: "1px dashed #000", boxSizing: "border-box" }} className="old-link">
                <tbody>
                  <tr>
                    <td style={{ padding: "10px", border: "1px dashed #000", boxSizing: "border-box" }}>
                      <a href="#" onClick={() => navigateToURL("http://internet.dom")}>
                        The Internet
                      </a>
                    </td>
                    <td style={{ padding: "10px", border: "1px dashed #000", boxSizing: "border-box" }}>
                      <a href="#" onClick={() => navigateToURL("http://ctf.dom")}>
                        CTF
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div style={{ paddingTop: "10px", display: "flex", gap: "8px", justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
                <img src="./img_era/fpcreated.gif" alt="Internet Explorer Logo" style={{ maxHeight: "40px", maxWidth: "80px" }} />
                <img src="./img_era/gc_icon.gif" alt="Internet Explorer Logo" style={{ maxHeight: "40px", maxWidth: "80px" }} />
                <img src="./img_era/ie_animated.gif" alt="Internet Explorer Logo" style={{ maxHeight: "40px", maxWidth: "80px" }} />
                <img src="./img_era/netscape.gif" alt="Internet Explorer Logo" style={{ maxHeight: "40px", maxWidth: "80px" }} />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
